import { JobPosition } from '@features/hireflixJob';
import {
  Videocam,
  VideocamOutlined,
  PeopleOutline,
  ChatBubbleOutline,
  SendOutlined,
} from '@mui/icons-material';
import {
  Stack,
  Typography,
  Button,
  Container,
  Grid,
  Tooltip,
} from '@mui/material';
import { Box } from '@mui/system';
import InterviewStyleModal from '@pages/HireflixIntegration/components/InterviewStyleModal';
import { hireflixCta, hireflixCtaHover, white } from '@styles/colors';
import { useState } from 'react';

import { hireflixTemplateQuestions as templateQuestions } from '../constants';
import HireflixAddVideoInterviewModal from './HireflixAddVideoInterviewModal';
import HireflixDeleteVideoInterviewModal from './HireflixDeleteVideoInterviewModal';
import HireflixUpdateVideoInterviewModal from './HireflixUpdateVideoInterviewModal';

const features = [
  {
    id: 1,
    icon: <PeopleOutline sx={{ color: '#a855f7' }} />,
    title: 'Select Positions',
    description: 'Choose positions for the interview',
  },
  {
    id: 2,
    icon: <ChatBubbleOutline sx={{ color: '#a855f7' }} />,
    title: 'Add Questions',
    description: 'Create your interview questions',
  },
  {
    id: 3,
    icon: (
      <SendOutlined sx={{ color: '#a855f7', transform: 'rotate(-45deg)' }} />
    ),
    title: 'Send Invites',
    description: 'Invite applicants to interview',
  },
];

const TemplateHireflixInterviewSettingsTab = ({ templateData }: any) => {
  const [selectedJob, setSelectedJob] = useState<JobPosition | null>(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [interviewStyleModal, setInterviewStyleModal] = useState(false);
  const [initialQuestions, setInitialQuestions] = useState<
    { title: string; description: string }[]
  >([]);
  const [showTemplateButtons, setShowTemplateButtons] = useState(false);

  const handleAddJobModel = () => {
    setShowTemplateButtons(false);
    setOpenAddModal(!openAddModal);
  };

  const handleOpenInterviewStyleModal = () => {
    setInterviewStyleModal(!interviewStyleModal);
  };

  const handleCloseInterviewStyleModal = () => {
    setInterviewStyleModal(false);
  };

  const handleInterviewStyleSelect = (styleId: number) => {
    if (styleId === 1) {
      setInitialQuestions([]);
      setShowTemplateButtons(true);
      setOpenAddModal(true);
    } else if (styleId === 2) {
      setInitialQuestions(
        templateQuestions.map((q) => ({
          title: q.title,
          description: q.description,
        })),
      );
      setShowTemplateButtons(false);
      setOpenAddModal(true);
    }

    setInterviewStyleModal(false);
  };

  const handleOpenDeleteModal = (job: JobPosition) => {
    setOpenDeleteModal(true);
    setSelectedJob(job);
  };

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false);
  };

  const handleOpenUpdateModal = (job: JobPosition) => {
    setSelectedJob(job);
    setOpenUpdateModal(true);
  };

  const handleCloseUpdateModal = () => {
    setOpenUpdateModal(false);
    setSelectedJob(null);
  };

  return (
    <Stack
      bgcolor={white}
      p={3}
      borderRadius={8}
      width="100%"
      sx={{
        minHeight: 'calc(100vh - 280px)',
        position: 'sticky',
        top: '20px',
      }}
    >
      {/* Header */}
      <Box
        bgcolor={white}
        pt={2}
        pb={4}
        px={2}
        mb={4}
        borderRadius={8}
        display="flex"
        alignItems="flex-start"
        flexWrap="wrap"
        gap={1}
        justifyContent="space-between"
      >
        <Box>
          <Typography
            variant="h2"
            fontSize="22px"
            fontWeight="bold"
            mb={1}
            display="flex"
            alignItems="center"
            gap={1.5}
          >
            <VideocamOutlined sx={{ fontSize: '23px' }} /> Hireflix Video
            Interviews
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Add your first Hireflix interview to start sending invites.
          </Typography>
        </Box>

        <Box display="flex" gap={3}>
          <Tooltip
            title={
              !templateData?.job_listing
                ? 'You’ll need to add template jobs before creating Hireflix interviews.'
                : ''
            }
            arrow
            placement="top"
            disableHoverListener={!!templateData?.job_listing}
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Videocam sx={{ fontSize: '16px !important' }} />}
                onClick={handleOpenInterviewStyleModal}
                disabled={!templateData?.job_listing}
                sx={{
                  fontSize: '14px',
                  px: 2,
                  backgroundColor: hireflixCta,
                  '&:hover': {
                    backgroundColor: hireflixCtaHover,
                  },
                }}
              >
                Create Interview
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box
        px={2}
        pb={4}
        sx={{
          minHeight: 'calc(100% - 142px)',
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {/* Video Interview Listing */}
        {templateData?.additional_info?.hireflix?.length > 0 && (
          <Box width={1} minHeight="inherit" flexGrow={1}>
            <Container disableGutters sx={{ maxWidth: '100% !important' }}>
              <Grid container spacing={4}>
                {templateData?.additional_info?.hireflix?.map(
                  (job: JobPosition) => (
                    <Grid item xs={12} sm={6} md={4} key={job.id}>
                      <Box
                        sx={{
                          backgroundColor: white,
                          borderRadius: 8,
                          p: 3,
                          pb: 4,
                          height: '100%',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                          border: '1px solid rgba(0, 0, 0, 0.08)',
                          transition: 'all 0.2s ease-in-out',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          '& .jobType': {
                            transition: 'color 0.35s ease',
                          },
                          '&:hover': {
                            boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                            transform: 'translateY(-3px)',
                            '& .jobType': {
                              color: 'rgb(0, 114, 163)',
                            },
                          },
                        }}
                      >
                        <Box>
                          <Typography
                            variant="h6"
                            fontSize="20px"
                            fontWeight={600}
                            mb={0.5}
                          >
                            {job.s_title}
                          </Typography>

                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ opacity: 0.8 }}
                          >
                            <strong className="jobType">{job.s_type}</strong> -
                            with {job.questions.length} Question
                            {job.questions.length > 1 && 's'}
                          </Typography>
                        </Box>

                        <Box
                          mt={5}
                          textAlign="right"
                          display="flex"
                          flexWrap="wrap"
                          gap={2}
                        >
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleOpenUpdateModal(job)}
                            sx={{ fontSize: '14px', pl: '12px', pr: '12px' }}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            sx={{ fontSize: '13px' }}
                            onClick={() => handleOpenDeleteModal(job)}
                          >
                            {' '}
                            Delete{' '}
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                  ),
                )}
              </Grid>
            </Container>
          </Box>
        )}

        {/* Empty State */}
        {!templateData?.additional_info?.hireflix && (
          <Box
            minHeight="inherit"
            textAlign="center"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Box>
              <Typography
                variant="body2"
                fontSize="16px"
                color="text.secondary"
              >
                <Typography variant="body1" fontSize="16px" fontWeight="700">
                  Enjoy 30 days of unlimited interviews, FREE.
                </Typography>
                Your trial kicks off when your first interview is recorded!
              </Typography>
              <Container
                disableGutters
                sx={{
                  maxWidth: '1024px !important',
                  width: '100% !important',
                  paddingTop: '24px',
                }}
              >
                <Grid container spacing={3}>
                  {features.map((item) => (
                    <Grid
                      item
                      xs={12}
                      sm={6}
                      md={4}
                      key={item.id}
                      display="flex"
                    >
                      <Box
                        component="article"
                        textAlign="center"
                        sx={{
                          padding: '24px',
                          border: '1px solid #e1e7ef',
                          borderRadius: '12px',
                          width: '100%',
                          boxShadow:
                            'rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 1px 2px 0px',
                        }}
                      >
                        <Box
                          component="div"
                          sx={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '50%',
                            backgroundColor: '#a855f71a',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 12px',
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Typography
                          variant="h6"
                          fontSize="16px"
                          lineHeight="16px"
                          fontWeight={600}
                          color="#001f3d"
                          mb="4px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Box
                            component="span"
                            sx={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              fontSize: '14px',
                              color: '#fff',
                              marginRight: '8px',
                              backgroundColor: '#a855f7',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {item.id}
                          </Box>
                          {item.title}
                        </Typography>

                        <Typography
                          variant="body2"
                          lineHeight="20px"
                          color="#65758b"
                        >
                          {item.description}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Container>
            </Box>
          </Box>
        )}
      </Box>

      {/* Add HireFlix Video Interview Modal */}
      {openAddModal && (
        <HireflixAddVideoInterviewModal
          open={openAddModal}
          onClose={handleAddJobModel}
          initialQuestions={initialQuestions}
          showTemplateButtons={showTemplateButtons}
          templateId={templateData?.id}
          currentJobs={templateData?.additional_info?.hireflix}
        />
      )}

      {/* Update HireFlix Video Interview Modal */}
      {openUpdateModal && (
        <HireflixUpdateVideoInterviewModal
          open={openUpdateModal}
          onClose={handleCloseUpdateModal}
          job={selectedJob}
          templateId={templateData?.id}
        />
      )}

      {/* Delete HireFlix Video Interview Modal */}
      {openDeleteModal && (
        <HireflixDeleteVideoInterviewModal
          open={openDeleteModal}
          selectedJob={selectedJob || null}
          onClose={handleCloseDeleteModal}
          templateId={templateData?.id}
        />
      )}

      {/* HireFlix Interview Style Modal */}
      {interviewStyleModal && (
        <InterviewStyleModal
          open={interviewStyleModal}
          onClose={handleCloseInterviewStyleModal}
          onSelect={handleInterviewStyleSelect}
        />
      )}
    </Stack>
  );
};

export default TemplateHireflixInterviewSettingsTab;
